import { formatPrice } from '../utils/utils';

export function initPredefinedCart() : void {
    const $predefinedCartForm = document.querySelector<HTMLFormElement>('.js-predefined-cart-form');

    if ($predefinedCartForm) {
        new PredefinedCartForm($predefinedCartForm);
    }
}

type ItemType = {
    $container: HTMLElement,
    optionalProductPrice: number,
    fields: {
        $declinations: NodeListOf<HTMLInputElement>,
        $faceToFaces: NodeListOf<HTMLInputElement>,
        $quantity: HTMLInputElement,
    },
    $strikedPriceContainer: HTMLElement|null,
    $unitPriceContainer: HTMLElement|null,
    $durationContainer: HTMLElement|null,
    $faceToFaceContainer: HTMLElement|null,
    $totalContainer: HTMLElement|null,
    $partnerCodeAlerts: NodeListOf<HTMLElement>,
};

class PredefinedCartForm {
    $form: HTMLFormElement;
    vatPercent: number;
    $totalContainers: NodeListOf<HTMLElement>;
    $vatTotalContainer: HTMLElement|null;
    $totalWithVatContainer: HTMLElement|null;
    items: ItemType[];

    constructor($form: HTMLFormElement) {
        this.$form = $form;
        this.vatPercent = parseInt(this.$form.dataset.vatPercent ?? '0');
        this.$totalContainers = this.$form.querySelectorAll<HTMLElement>('.js-total');
        this.$vatTotalContainer = this.$form.querySelector<HTMLElement>('.js-vat-total');
        this.$totalWithVatContainer = this.$form.querySelector<HTMLElement>('.js-total-with-vat');
        this.items = Array.from(this.$form.querySelectorAll<HTMLElement>('.js-product')).map($item => {
            return {
                $container: $item,
                optionalProductPrice: parseInt($item.querySelector<HTMLInputElement>('.js-item-optional-product-field')?.value ?? '0'),
                fields: {
                    $declinations: $item.querySelectorAll<HTMLInputElement>('.js-item-declination-field'),
                    $faceToFaces: $item.querySelectorAll<HTMLInputElement>('.js-item-face-to-face-field'),
                    $quantity: $item.querySelector('.js-item-quantity-field') as HTMLInputElement,
                },
                $strikedPriceContainer: $item.querySelector<HTMLElement>('.js-item-striked-price'),
                $unitPriceContainer: $item.querySelector<HTMLElement>('.js-item-unit-price'),
                $durationContainer: $item.querySelector('.js-item-duration'),
                $faceToFaceContainer: $item.querySelector('.js-item-face-to-face'),
                $totalContainer: $item.querySelector<HTMLElement>('.js-item-total-price'),
                $partnerCodeAlerts: $item.querySelectorAll<HTMLElement>('.js-partner-code-alert'),
            };
        });

        this.calcAndApplyTotal();
        this.items.flatMap(item => this.getAllFieldsOfItem(item)).forEach($priceField => {
            $priceField.addEventListener('change', () => {
                this.calcAndApplyTotal();
            });
        });
    }

    /**
     * Return all fields of a item
     * 
     * @param {ItemType} item
     * 
     * @return {HTMLInputElement[]}
     */
    getAllFieldsOfItem(item: ItemType) : HTMLInputElement[] {
        return [...Array.from(item.fields.$declinations), ...Array.from(item.fields.$faceToFaces), item.fields.$quantity];
    }

    calcAndApplyTotalForItem(item: ItemType) : number {
        const $selectedDuration = Array.from(item.fields.$declinations)
            .filter($field => $field.checked || 'hidden' === $field.type).pop();

        const $selectedFaceToFace = Array.from(item.fields.$faceToFaces)
            .filter($field => $field.checked || 'hidden' === $field.type).pop();

        const strikedPrice = $selectedDuration ? parseInt($selectedDuration.dataset.strikedPrice ?? '0') : 0;
        const unitPrice = $selectedDuration ? parseInt($selectedDuration.dataset.price ?? '0') : 0;
        const faceToFacePrice = $selectedFaceToFace ? parseInt($selectedFaceToFace.dataset.price ?? '0') : 0;
        const quantity = parseInt(item.fields.$quantity.value);
        const totalPrice = (unitPrice + faceToFacePrice + item.optionalProductPrice) * quantity;

        if (item.$durationContainer) {
            item.$durationContainer.innerText = $selectedDuration?.dataset.duration ?? '-';
        }

        if (item.$faceToFaceContainer) {
            item.$faceToFaceContainer.innerText = $selectedFaceToFace?.dataset.faceToFace ?? '-';

            if ($selectedFaceToFace) {
                item.$faceToFaceContainer.innerText += ' (' + (faceToFacePrice ? (formatPrice(faceToFacePrice) + ' €') : ($selectedFaceToFace?.dataset.faceToFaceFree ?? '')) + ')';
            }
        }

        if (item.$strikedPriceContainer) {
            item.$strikedPriceContainer.innerText = strikedPrice !== unitPrice && strikedPrice !== 0 ? formatPrice(strikedPrice) : '';
        }

        if (item.$unitPriceContainer) {
            item.$unitPriceContainer.innerText = formatPrice(unitPrice);
        }

        if (item.$totalContainer) {
            item.$totalContainer.innerText = formatPrice(totalPrice);
        }

        const $declinationAlerts = Array.from(item.$partnerCodeAlerts).filter($alert => {
            return $alert.dataset.type === 'declination';
        });
        const $faceToFaceAlerts = Array.from(item.$partnerCodeAlerts).filter($alert => {
            return $alert.dataset.type === 'face_to_face';
        });

        // Display violation alert about the duration selected
        $declinationAlerts.forEach($alert => {
            $alert.classList.toggle('hidden', !$selectedDuration || $selectedDuration.value !== $alert.dataset.ref);
        });

        const declinationAlertDisplayed = !!$declinationAlerts.filter($alert => {
            return !$alert.classList.contains('hidden');
        }).length;

        // Display violation alert about the face to face selected only if no declination alert are displayed
        $faceToFaceAlerts.forEach($alert => {
            $alert.classList.toggle('hidden', declinationAlertDisplayed || !$selectedFaceToFace || $selectedFaceToFace.value !== $alert.dataset.ref);
        });

        return totalPrice;
    }

    /**
     * Calculate and apply the total
     */
    calcAndApplyTotal() : void {
        const total = this.items
            .map(item => this.calcAndApplyTotalForItem(item))
            .reduce((accumulator, currentValue) => accumulator + currentValue, 0);
        const vatTotal = this.vatPercent * total / 100;
        const totalWithVat = total + vatTotal;

        this.$totalContainers.forEach($totalContainer => $totalContainer.innerText = formatPrice(total));

        if (this.$vatTotalContainer) {
            this.$vatTotalContainer.innerText = formatPrice(vatTotal);
        }

        if (this.$totalWithVatContainer) {
            this.$totalWithVatContainer.innerText = formatPrice(totalWithVat);
        }
    }
}
